<script setup lang="ts">
import { onMounted } from 'vue';
import { Zap } from 'lucide-vue-next';
import { deliveryteamOrdersApiClient } from "@api-client/deliveryteam_orders";
import { Button } from "@/components/ui/button";

const isDev = import.meta.env.DEV;
const props = defineProps<{
    deliveryteamOrderId: string;
}>();
onMounted(() => {
    console.log("DebugOrderActions, props.deliveryteamOrderId", props.deliveryteamOrderId);
});

const processPartnerBalancesForOrder = async () => {
    const response = await (await deliveryteamOrdersApiClient.dev_test.process_partner_balances_for_order.$post({
        json: { deliveryteamOrderId: props.deliveryteamOrderId },
    })).json();
    console.log("DebugOrderActions, processPartnerBalancesForOrder response", response);
};

const runCron = async (action: 'cancelOldOrders_AbandonedByDeliveryteam_CronHandler' | 'autoTopUpDeliveryteamBalance_CronHandler' | 'checkAndCompleteOrders_After24Hours_CronHandler_Deliveryteam' | 'checkAndPlaceScheduledOrders_CronHandler_Deliveryteam' | 'sendReminder_ForNotFinished_OnlinePayments_CronHandler_Deliveryteam' | 'expireOldPayments_CronHandler_Deliveryteam' | 'processPartnerBalances_CronHandler_Deliveryteam' | 'checkAndCaptureStripeOrders_After4Hours_CronHandler_Deliveryteam'
) => {
    const response = await (await deliveryteamOrdersApiClient.dev_test.run_cron.$post({
        json: { action },
    })).json();
    console.log("DebugOrderActions, response", response);
};

const updateTs = async (
    column: 'draft_create_at_ts' | 'order_placed_at_ts' | 'scheduled_at_ts',
    addMinutes: number
) => {
    const response = await (await deliveryteamOrdersApiClient.dev_test.update_ts.$post({
        json: {
            deliveryteamOrderId: props.deliveryteamOrderId,
            ts: Date.now() + (addMinutes * 60 * 1000),
            column
        },
    })).json();
    console.log("DebugOrderActions, response", response);
};

const runClass = async (action:
    'DeliveryteamAcceptOrderClass' |
    'DeliveryteamCancelOrderClass' |
    'DeliveryteamCompleteOrderClass' |
    'StripeVerifyDeliveryteamOrderClass' |
    'DeliveryteamPlaceScheduledOrderClass' |
    'PaypalVerifyDeliveryteamOrderClass' |
    'SApiKeyVerifyDeliveryteamOrderClass' |
    'DeliveryteamExpireOrderClass' |
    'StripeCaptureDeliveryteamOrderClass'
) => {
    const response = await (await deliveryteamOrdersApiClient.dev_test.run_class.$post({
        json: { deliveryteamOrderId: props.deliveryteamOrderId, action },
    })).json();
    console.log("DebugOrderActions, response", response.data);
};
const times = [(-60 * 24 * 3), (-60 * 24), -245, -65, -60, -25, -15, -10, -5, 0]
</script>

<template>
    <details v-if="isDev">
        <summary>
            <Zap class="h-5 w-5 text-yellow-500 inline-block cursor-pointer" />
            Debug Actions
        </summary>
        <div class="space-y-6 p-4 border rounded-lg">
            <div>
                <div class="text-xs">draft_create_at_ts</div>
                <span @click="updateTs('draft_create_at_ts', i)"
                      class="cursor-pointer text-xs border rounded-md p-1 inline-flex"
                      v-for="i in times">
                    {{ (Math.abs(i) > 120) ? (i / 60).toFixed(2) + "h" : i + "m" }}
                </span>
            </div>
            <div>
                <div class="text-xs">order_placed_at_ts</div>
                <span @click="updateTs('order_placed_at_ts', i)"
                      class="cursor-pointer text-xs border rounded-md p-1 inline-flex"
                      v-for="i in times">
                    {{ (Math.abs(i) > 120) ? (i / 60).toFixed(2) + "h" : i + "m" }}
                </span>
            </div>
            <div>
                <div class="text-xs">scheduled_at_ts</div>
                <span @click="updateTs('scheduled_at_ts', i)"
                      class="cursor-pointer text-xs border rounded-md p-1 inline-flex"
                      v-for="i in times">
                    {{ (Math.abs(i) > 120) ? (i / 60).toFixed(2) + "h" : i + "m" }}
                </span>
            </div>

            <div class="flex flex-col gap-2 mt-4 border-t pt-4">
                <h3 class="text-lg font-bold">Class Actions</h3>
            </div>
            <div class="">
                <Button @click="runClass('DeliveryteamAcceptOrderClass')"
                        variant="secondary"
                        class="m-1">
                    DeliveryteamAcceptOrderClass
                </Button>
                <Button @click="runClass('DeliveryteamCancelOrderClass')"
                        variant="secondary"
                        class="m-1">
                    DeliveryteamCancelOrderClass
                </Button>
                <Button @click="runClass('DeliveryteamCompleteOrderClass')"
                        variant="secondary"
                        class="m-1">
                    DeliveryteamCompleteOrderClass
                </Button>
                <Button @click="runClass('StripeVerifyDeliveryteamOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StripeVerifyDeliveryteamOrderClass
                </Button>
                <Button @click="runClass('PaypalVerifyDeliveryteamOrderClass')"
                        variant="secondary"
                        class="m-1">
                    PaypalVerifyDeliveryteamOrderClass
                </Button>
                <Button @click="runClass('SApiKeyVerifyDeliveryteamOrderClass')"
                        variant="secondary"
                        class="m-1">
                    SApiKeyVerifyDeliveryteamOrderClass
                </Button>
                <Button @click="runClass('DeliveryteamPlaceScheduledOrderClass')"
                        variant="secondary"
                        class="m-1">
                    DeliveryteamPlaceScheduledOrderClass
                </Button>
                <Button @click="runClass('DeliveryteamExpireOrderClass')"
                        variant="secondary"
                        class="m-1">
                    DeliveryteamExpireOrderClass
                </Button>
                <Button @click="runClass('StripeCaptureDeliveryteamOrderClass')"
                        variant="secondary"
                        class="m-1">
                    StripeCaptureDeliveryteamOrderClass
                </Button>
                <Button @click="processPartnerBalancesForOrder()"
                        variant="secondary"
                        class="m-1">
                    Process Partner Balances (skip 14d wait)
                </Button>
            </div>


            <div class="flex flex-col gap-2 mt-4 border-t pt-4">
                <h3 class="text-lg font-bold">Cron Actions</h3>
            </div>
            <div>
                <Button @click="runCron('cancelOldOrders_AbandonedByDeliveryteam_CronHandler')"
                        variant="secondary"
                        class="m-1">
                    Cancel Old Orders
                </Button>
                <Button @click="runCron('autoTopUpDeliveryteamBalance_CronHandler')"
                        variant="secondary"
                        class="m-1">
                    Auto Top-up Deliveryteam Balance
                </Button>
                <Button @click="runCron('checkAndCompleteOrders_After24Hours_CronHandler_Deliveryteam')"
                        variant="secondary"
                        class="m-1">
                    Complete Orders
                </Button>
                <Button @click="runCron('checkAndPlaceScheduledOrders_CronHandler_Deliveryteam')"
                        variant="secondary"
                        class="m-1">
                    Place Scheduled Orders
                </Button>
                <Button @click="runCron('sendReminder_ForNotFinished_OnlinePayments_CronHandler_Deliveryteam')"
                        variant="secondary"
                        class="m-1">
                    Send Reminder
                </Button>
                <Button @click="runCron('expireOldPayments_CronHandler_Deliveryteam')"
                        variant="secondary"
                        class="m-1">
                    Expire Old Payments
                </Button>
                <Button @click="runCron('processPartnerBalances_CronHandler_Deliveryteam')"
                        variant="secondary"
                        class="m-1">
                    processPartnerBalances_CronHandler_Deliveryteam
                </Button>
                <Button @click="runCron('checkAndCaptureStripeOrders_After4Hours_CronHandler_Deliveryteam')"
                        variant="secondary"
                        class="m-1">
                    Capture Stripe Orders
                </Button>
            </div>

        </div>
    </details>
</template>